import type { Trace } from '@/features/traceability/type/Tool';
import type { AggregationKey } from './aggregations';

export type MetricDef = {
  key: string;
  labelKey: string;
  unit?: string;
  kind: 'numeric' | 'categorical';
  defaultAggregation: AggregationKey;
  supportedAggregations: AggregationKey[];
  extract: (trace: Trace) => number | string | null;
};

// Traces carry an optional reference field injected by the comparison pipeline
type TraceWithReference = Trace & { reference?: string };

export const metricRegistry: MetricDef[] = [
  // ── Numeric: duration ────────────────────────────────────────────────────
  {
    key: 'totalMachiningDuration',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.totalMachiningDuration',
    // Seconds (as stored); formatted by formatDuration, which appends its own s/min/h unit.
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.totalMachiningDuration,
  },
  {
    key: 'pieceMachiningDuration',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.pieceMachiningDuration',
    // Seconds (as stored); formatted by formatDuration, which appends its own s/min/h unit.
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.pieceMachiningDuration,
  },

  // ── Numeric: volume & distance ───────────────────────────────────────────
  {
    key: 'machinedVolume',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.machinedVolume',
    unit: 'cm³',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.machinedVolume,
  },
  {
    key: 'machinedDistance',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.machinedDistance',
    unit: 'm',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.machinedDistance,
  },

  // ── Numeric: count ───────────────────────────────────────────────────────
  {
    key: 'machinedPieces',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.machinedPieces',
    kind: 'numeric',
    defaultAggregation: 'sum',
    supportedAggregations: ['sum', 'avg', 'mean', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.machinedPieces,
  },

  // ── Numeric: measurement ─────────────────────────────────────────────────
  {
    key: 'malround',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.malround',
    unit: 'mm',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.malround,
  },

  // ── Numeric: cutting parameters ──────────────────────────────────────────
  {
    key: 'vc',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.vc',
    unit: 'm/min',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.vc,
  },
  {
    key: 'fz',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.fz',
    unit: 'mm/z',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.fz,
  },
  {
    key: 'ap',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.ap',
    unit: 'mm',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.ap,
  },
  {
    key: 'ae',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.ae',
    unit: 'mm',
    kind: 'numeric',
    defaultAggregation: 'avg',
    supportedAggregations: ['avg', 'mean', 'sum', 'median', 'min', 'max', 'count', 'p90', 'p95'],
    extract: (t) => t.ae,
  },

  // ── Categorical ──────────────────────────────────────────────────────────
  {
    key: 'machine',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.machine',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.machine?.name ?? null,
  },
  {
    key: 'material',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.material',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.material?.name ?? null,
  },
  {
    key: 'lubrication',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.lubrication',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.lubType ?? null,
  },
  {
    key: 'toolHolder',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.toolHolder',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.holderType ?? null,
  },
  {
    key: 'operator',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.operator',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.operator ?? null,
  },
  {
    key: 'toolId',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.toolId',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.toolId,
  },
  {
    key: 'traceId',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.traceId',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => (t.traceId != null ? String(t.traceId) : null),
  },
  {
    key: 'reference',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.reference',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => (t as TraceWithReference).reference ?? null,
  },
  {
    key: 'operation',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.operation',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.operation ?? null,
  },
  {
    key: 'toolState',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.toolState',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.toolState?.toolState ?? null,
  },
  {
    key: 'clampingType',
    labelKey: 'analytics.dashboard.complexCharts.configuration.metrics.clampingType',
    kind: 'categorical',
    defaultAggregation: 'count',
    supportedAggregations: ['count'],
    extract: (t) => t.clampingType ?? null,
  },
];

export type MetricKey = (typeof metricRegistry)[number]['key'];
